import { useCallback, useEffect, useMemo, useRef } from 'react';
import { debounce } from '@visx/responsive';
import { useTooltip } from '@visx/tooltip';
import TooltipContext from "../context/TooltipContext.js";
import isValidNumber from "../typeguards/isValidNumber.js";
import { jsx as _jsx } from "react/jsx-runtime";
/** Simple wrapper around useTooltip, to provide tooltip data via context. */
export default function TooltipProvider(_ref) {
  let {
    hideTooltipDebounceMs = 400,
    children
  } = _ref;
  const {
    tooltipOpen,
    tooltipLeft,
    tooltipTop,
    tooltipData,
    updateTooltip,
    hideTooltip: privateHideTooltip
  } = useTooltip(undefined);
  const debouncedHideTooltip = useRef(null);
  function cancelDeboundedHideTooltip() {
    if (debouncedHideTooltip.current) {
      debouncedHideTooltip.current.cancel();
      debouncedHideTooltip.current = null;
    }
  }
  useEffect(() => cancelDeboundedHideTooltip, []);
  const showTooltip = useRef(_ref2 => {
    let {
      svgPoint,
      index,
      key,
      datum,
      distanceX,
      distanceY
    } = _ref2;
    // cancel any hideTooltip calls so it won't hide after invoking the logic below
    cancelDeboundedHideTooltip();
    const cleanDistanceX = isValidNumber(distanceX) ? distanceX : Infinity;
    const cleanDistanceY = isValidNumber(distanceY) ? distanceY : Infinity;
    const distance = Math.sqrt(cleanDistanceX ** 2 + cleanDistanceY ** 2);
    updateTooltip(_ref3 => {
      let {
        tooltipData: currData
      } = _ref3;
      const currNearestDatumDistance = currData?.nearestDatum && isValidNumber(currData.nearestDatum.distance) ? currData.nearestDatum.distance : Infinity;
      return {
        tooltipOpen: true,
        tooltipLeft: svgPoint?.x,
        tooltipTop: svgPoint?.y,
        tooltipData: {
          nearestDatum: (currData?.nearestDatum?.key ?? '') !== key && currNearestDatumDistance < distance ? currData?.nearestDatum : {
            key,
            index,
            datum,
            distance
          },
          datumByKey: {
            ...currData?.datumByKey,
            [key]: {
              datum,
              index,
              key
            }
          }
        }
      };
    });
  });
  const hideTooltip = useCallback(() => {
    debouncedHideTooltip.current = debounce(privateHideTooltip, hideTooltipDebounceMs);
    debouncedHideTooltip.current();
  }, [privateHideTooltip, hideTooltipDebounceMs]);
  const value = useMemo(() => ({
    tooltipOpen,
    tooltipLeft,
    tooltipTop,
    tooltipData,
    updateTooltip,
    showTooltip: showTooltip.current,
    hideTooltip
  }), [hideTooltip, tooltipData, tooltipLeft, tooltipOpen, tooltipTop, updateTooltip]);
  return /*#__PURE__*/_jsx(TooltipContext.Provider, {
    value: value,
    children: children
  });
}